import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useCallback } from 'react'
import {
  CreateAgreementTemplateDetails,
  CreateAgreementTemplateDetailsError,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { ENTITY_TYPES } from '@sales-center/common'
import { FormPacketErrorAlert } from '@user/components/Agreements/AgreementFormPacketDialog/FormPacketErrorAlert'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useAgreementPacketCardActions } from '@user/components/Agreements/AgreementFormPacketDialog/hooks/useAgreementPacketCardActions'
import { CreateLocationFormPacketError } from '@user/components/Agreements/CreateAgreementDrawer/CreateAgreementForm/TemplateDetails/CreateLocationFormPacketError'

type Props = {
  agreement: CreateAgreementTemplateDetails
  formError?: CreateAgreementTemplateDetailsError
  openServicesDrawer: () => void
  entityId: number
  entityType: ENTITY_TYPES
}

export function DraggableFormPacketErrors(props: Props) {
  const { agreement, formError, openServicesDrawer, entityType } = props
  const { handleDetailInclude } = useAgreementPacketCardActions(entityType)

  const areServiceSlotsInvalid =
    formError?.exceedsServiceSlots && !formError?.exceedsServiceLocations
  const areServiceLocationsInvalid = formError?.exceedsServiceLocations

  const spaceNotEnough = areServiceSlotsInvalid || areServiceLocationsInvalid

  const summaryError =
    agreement.dynamicServiceBlock && !agreement.willDynamicServiceBlockFit

  const onChange = useCallback(
    (_, checked: boolean) => handleDetailInclude(agreement.templateId, checked),
    [agreement.templateId, handleDetailInclude],
  )

  return (
    <Stack
      sx={{
        marginTop: '12px',
        marginX: '16px',
      }}
    >
      {formError?.showPestPacLocationError ? (
        <Stack width="100%">
          <CreateLocationFormPacketError
            {...props}
            showPestPacLocationError={formError?.showPestPacLocationError}
          />
        </Stack>
      ) : (
        <>
          {spaceNotEnough && (
            <Stack width="100%">
              <FormPacketErrorAlert
                title="Insufficient Space on Template"
                description={
                  areServiceSlotsInvalid
                    ? 'The template selected does not have enough slots for the services added.'
                    : 'The template selected does not support multiple service locations.'
                }
              >
                <Box sx={{ marginTop: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    Remove this template and select another or{' '}
                    <Typography
                      variant="body2"
                      component={'span'}
                      sx={{
                        color: '#1565C0',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                      onClick={openServicesDrawer}
                    >
                      Click here
                    </Typography>{' '}
                    to edit Services.
                  </Typography>
                </Box>
              </FormPacketErrorAlert>
            </Stack>
          )}

          {summaryError && (
            <Stack width="100%">
              <FormPacketErrorAlert
                title="Service Details Replaced with Summary"
                description={
                  'There is not enough space on this template for the service details. Select the box below to include the details as an extra page.'
                }
                type="info"
              >
                <Box sx={{ marginTop: '8px' }}>
                  <FormControlLabel
                    sx={{ marginLeft: 0, marginBottom: '0px' }}
                    onChange={onChange}
                    label={
                      <Typography variant="body2">
                        Include Service Details on a new page
                      </Typography>
                    }
                    control={
                      <Checkbox
                        color="primary"
                        checked={agreement.includeDetail}
                      />
                    }
                  />
                </Box>
              </FormPacketErrorAlert>
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}
